// Umbraco Engage custom events - declaratively via data attributes, or from code.
//
//   <a href="…" data-engage-category="CTA" data-engage-action="Click" data-engage-label="Contact us">
//   trackEngageEvent({ category: "Contact", action: "Open", label: name });
//
// category + action are required; label and value (a number - the only numeric field Engage
// supports) are optional. The attribute version fires on click (keyboard activation of links and
// buttons dispatches click too) and on auxclick (middle-click opening a link in a new tab).
//
// Consent: umbracoEngage.analytics.js is only rendered when ModulePermissions allows analytics
// (Layout.cshtml), so the script being on the page IS the consent check - without it everything
// here is a no-op.
//
// Links: Engage sends each event immediately with navigator.sendBeacon, which the browser
// completes even after the page unloads. Sending it from the click handler - which always runs
// before the browser navigates - is enough; no preventDefault/delayed navigation needed.
//
// Tracking must never break the rest of the site's JS: nothing here throws, waits on Engage or
// blocks a click. If Engage is blocked (ad blocker), 404s or throws, events are dropped.

export interface EngageEvent {
    category: string;
    action: string;
    label?: string;
    value?: number;
}

declare global {
    interface Window {
        umbEngage?: (...args: unknown[]) => void;
    }
}

export const ENGAGE_SCRIPT_SELECTOR = "script[data-engage-analytics]";
const TRIGGER_SELECTOR = "[data-engage-category][data-engage-action]";

// Events tracked before Engage's script has run (main.js runs before it - both are deferred, and
// main.js comes first in the layout). Capped, so a script that never arrives can't grow it.
const MAX_QUEUED_EVENTS = 20;
const queue: EngageEvent[] = [];
// Set by initEngageEvents when Engage's script is on the page (= analytics consent), cleared if it
// fails to load. Events are only queued while it's true.
let engageExpected = false;

function send(event: EngageEvent): void {
    // Optional trailing args are left off rather than passed as undefined, matching Engage's
    // positional GA-style signature: ("send", "event", category, action, label, value).
    const args: unknown[] = ["send", "event", event.category, event.action];
    if (event.label !== undefined || event.value !== undefined) {
        args.push(event.label ?? "");
    }
    if (event.value !== undefined) {
        args.push(event.value);
    }
    window.umbEngage!(...args);
}

function flushQueue(): void {
    try {
        while (queue.length > 0 && window.umbEngage) {
            send(queue.shift()!);
        }
    } catch (error) {
        queue.length = 0;
        console.warn("Engage event tracking failed", error);
    }
}

export function trackEngageEvent(event: EngageEvent): void {
    try {
        if (window.umbEngage) {
            send(event);
        } else if (engageExpected && queue.length < MAX_QUEUED_EVENTS) {
            queue.push(event);
        }
    } catch (error) {
        console.warn("Engage event tracking failed", error);
    }
}

function eventFromElement(element: Element): EngageEvent | null {
    const category = element.getAttribute("data-engage-category")?.trim();
    const action = element.getAttribute("data-engage-action")?.trim();
    if (!category || !action) {
        return null;
    }

    const engageEvent: EngageEvent = { category, action };

    const label = element.getAttribute("data-engage-label")?.trim();
    if (label) {
        engageEvent.label = label;
    }

    const rawValue = element.getAttribute("data-engage-value")?.trim();
    const value = rawValue ? Number(rawValue) : NaN;
    if (Number.isFinite(value)) {
        engageEvent.value = value;
    }

    return engageEvent;
}

function onClick(event: MouseEvent): void {
    try {
        // auxclick also fires for right-click (context menu) - only middle-click opens a link.
        if (event.type === "auxclick" && event.button !== 1) {
            return;
        }
        const element = (event.target as Element | null)?.closest?.(TRIGGER_SELECTOR);
        // The click that ends a drag in a sideways-scrolling row is swallowed by scroll-row-wheel.ts
        // - but only after this capture listener on document has run, so skip it here.
        if (element?.closest(".scroll-row.is-dragging")) {
            return;
        }
        // Middle-click does nothing on a button (e.g. a contact card opening a dialog) - only a link
        // opens in a new tab.
        if (event.type === "auxclick" && !element?.matches("a[href]")) {
            return;
        }
        const engageEvent = element ? eventFromElement(element) : null;
        if (engageEvent) {
            trackEngageEvent(engageEvent);
        }
    } catch (error) {
        console.warn("Engage event tracking failed", error);
    }
}

export function initEngageEvents(): void {
    const script = document.querySelector<HTMLScriptElement>(ENGAGE_SCRIPT_SELECTOR);
    if (script) {
        engageExpected = true;
        // Not umbracoEngageAnalyticsReady: Engage dispatches it inside the function whose return
        // value becomes window.umbEngage, so window.umbEngage is still undefined at that point.
        // The script's load event fires after it has run.
        script.addEventListener("load", flushQueue);
        // Blocked or missing script: drop anything queued and stop queueing.
        script.addEventListener("error", () => {
            engageExpected = false;
            queue.length = 0;
        });
    }

    // Delegated, so elements rendered later (contact cards, listings) work too. Capture phase, so
    // a component calling stopPropagation doesn't silence tracking.
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
}
